import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { NgIcon } from '@ng-icons/core';
import { injectViewportBreakpoints } from '@quipex/core/utils';
import { take } from 'rxjs';
import { QpxButton, QpxIcon, QpxTooltip } from '../../directives';
import { injectPopover, type PopoverRef } from '../../services';

export type QpxSortAppearance = 'elevated' | 'outlined';

export interface QpxSortParameter<TField extends string = string> {
    id: string;
    label: string;
    sortByLabel: string;
    field: TField;
}

export interface QpxSortSelection<TField extends string = string> {
    active: TField;
    direction: 'asc' | 'desc';
}

@Component({
    selector: 'qpx-sort',
    imports: [
        MatButtonModule,
        MatMenuModule,
        QpxTooltip,
        NgIcon,
        QpxButton,
        QpxIcon,
    ],
    templateUrl: './sort.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'inline-flex',
        '[class.cursor-not-allowed]': 'disabled()',
    },
})
export class QpxSort {
    //#region Dependency injection
    private readonly destroyRef = inject(DestroyRef);
    private readonly popover = injectPopover();
    private readonly viewport = injectViewportBreakpoints();
    //#endregion

    //#region Inputs
    public readonly parameters = input.required<readonly QpxSortParameter[]>();
    public readonly activeSort = input.required<QpxSortSelection>();
    public readonly defaultSort = input.required<QpxSortSelection>();
    public readonly appearance = input<QpxSortAppearance>('elevated');
    public readonly disabled = input(false);
    public readonly disabledReason = input<string | null>(null);
    public readonly entityLabel = input.required<string>();
    public readonly iconOnly = input(false);
    public readonly menuAriaLabel = input.required<string>();
    public readonly defaultSortApplied = input(false);
    //#endregion

    //#region Outputs
    public readonly sortChanged = output<QpxSortSelection>();
    //#endregion

    //#region State
    protected readonly isLaptop = this.viewport.atLeast('laptop');
    protected readonly isCompactPopoverOpen = signal(false);
    protected readonly selectedSort = computed<QpxSortSelection>(() => {
        const activeSort = this.activeSort();

        if (!this.hasActiveSort()) {
            return this.defaultSort();
        }

        return activeSort;
    });

    protected readonly hasActiveSort = computed(() => {
        if (this.defaultSortApplied()) {
            return true;
        }

        const activeSort = this.activeSort();
        const fields = new Set(
            this.parameters().map((parameter) => parameter.field)
        );

        return fields.has(activeSort.active);
    });

    protected readonly selectedParameter = computed(() => {
        const selectedSort = this.selectedSort();

        return (
            this.parameters().find(
                (parameter) => parameter.field === selectedSort.active
            ) ?? this.parameters()[0]
        );
    });

    protected readonly directionLabel = computed(() =>
        this.selectedSort().direction === 'desc' ? 'descending' : 'ascending'
    );

    protected readonly directionIcon = computed(() =>
        this.selectedSort().direction === 'desc'
            ? 'lucideSortDesc'
            : 'lucideSortAsc'
    );

    protected readonly sortByLabel = computed(
        () => this.selectedParameter()?.sortByLabel ?? ''
    );
    private compactPopoverRef: PopoverRef | undefined;
    //#endregion

    constructor() {
        this.destroyRef.onDestroy(() => this.closeCompactPopover());

        effect(() => {
            if ((!this.isLaptop() || this.iconOnly()) && !this.disabled())
                return;

            this.closeCompactPopover();
        });
    }

    //#region Template methods
    protected toggleCompactPopover(event: MouseEvent): void {
        if (this.disabled()) return;

        if (this.compactPopoverRef && !this.compactPopoverRef.closed()) {
            this.compactPopoverRef.close();
            return;
        }

        const origin = event.currentTarget;
        if (!(origin instanceof HTMLButtonElement)) return;

        const popoverRef = this.popover.open({
            origin,
            closeOnOutsidePointer: true,
            loadComponent: async () =>
                (await import('./sort-popover')).QpxSortPopover,
            componentInputs: {
                parameters: this.parameters(),
                selectedSort: this.selectedSort(),
                entityLabel: this.entityLabel(),
                menuAriaLabel: this.menuAriaLabel(),
                sortChanged: (selection: QpxSortSelection) =>
                    this.sortChanged.emit(selection),
            },
        });

        this.compactPopoverRef = popoverRef;
        this.isCompactPopoverOpen.set(true);
        popoverRef
            .afterClosed()
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.compactPopoverRef === popoverRef) {
                    this.compactPopoverRef = undefined;
                    this.isCompactPopoverOpen.set(false);
                }

                const trigger = origin;
                setTimeout(() => {
                    const activeElement = document.activeElement;
                    const focusWasOrphaned =
                        activeElement === null ||
                        activeElement === document.body ||
                        !activeElement.isConnected;

                    if (trigger.isConnected && focusWasOrphaned) {
                        trigger.focus();
                    }
                });
            });
    }

    protected toggleDirection(): void {
        if (this.disabled()) return;

        const selectedSort = this.selectedSort();

        if (!this.hasActiveSort()) {
            this.sortChanged.emit(selectedSort);
            return;
        }

        this.sortChanged.emit({
            active: selectedSort.active,
            direction: selectedSort.direction === 'desc' ? 'asc' : 'desc',
        });
    }

    protected selectParameter(parameter: QpxSortParameter): void {
        if (this.disabled()) return;

        this.sortChanged.emit({
            active: parameter.field,
            direction: this.selectedSort().direction,
        });
    }
    //#endregion

    //#region Private methods
    private closeCompactPopover(): void {
        this.compactPopoverRef?.close();
    }
    //#endregion
}
